import React, { useEffect, useState } from "react";
import {
  Page,
  Layout,
  Frame,
  BlockStack,
  Text,
  Link
} from "@shopify/polaris";
import { buildAppEmbedExpandDeepLink } from "../../utils/ShopifyAdminLinkTools";
import { GET_SALE_STATISTICS } from "../../graphql/Queries/TaskStatus";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ONBOARDING_STATUS } from "../../graphql/Queries/Settings";
import { SAVE_ONBOARDING_STATUS } from "../../graphql/Mutations/Settings";
import "./DashboardPage.css";
import { AppRatingWidget } from "../../components/Review"
import { openBeacon } from "../../utils/BeaconUtils";
import { useNavigate } from "react-router-dom";
import SaleTypeModal from "../../components/Modals/SaleTypeModal";
import { getSaleTypeUrlParam } from "../../features/DiscountPlanning/Discount";
import DraftResumeBanner from "../../components/Banners/DraftResumeBanner";
import ThemeStatus from "../../components/ThemeStatus";
import BFCMSaleBanner from "../../components/Banners/BFCMSaleBanner";
import HalloweenOfferBanner from "../../components/Banners/HalloweenOfferBanner";
import SecurityNoticeBanner from "../../components/Banners/SecurityNoticeBanner";
import useThemeExtensionStatus from "../../hooks/useThemeExtensionStatus";
import CampaignOverview from "./components/CampaignOverview";
import SetupGuide from "./components/SetupGuide";
import RecentCampaigns from "./components/RecentCampaigns";
import HelpCard from "./components/HelpCard";

// Setup guide steps in display order; stepCompletions and the saved onboarding flags use these positions.
const STEP = {
  appEmbed: 0,
  firstDiscount: 1,
  customizeDesign: 2,
  blockPlacement: 3,
  cartDiscountBlock: 4,
  featuredCollections: 5,
};

const NON_OS2_PLACEMENT_ARTICLE = "https://productessentials.helpscoutdocs.com/article/42-how-to-setup-custom-placement-for-countdown-timer-and-announcement-bar-in-non-online-store-2-0-compatible-shopify-themes";
const OFFER_DISCOUNT_IMAGE = "https://cdn.shopify.com/shopifycloud/shopify/assets/admin/home/onboarding/home-guides-offer-discount-b1e850100e6b3b773f1abf54cf50c246a59eca429e7684e189385b6752fe07a8.svg";
const CUSTOMIZE_THEME_IMAGE = "https://cdn.shopify.com/shopifycloud/shopify/assets/admin/home/onboarding/detail-images/customize-theme-task-d2760705452ef967a48b5fc4a6dce1ea9fc2fe4566dea18bb0927a746abb349f.svg";

// The button the merchant has to click in Shopify, bold so a step skims as "what to click"
const ClickTarget = ({ children }) => <Text as="span" fontWeight="semibold" tone="base">{children}</Text>;

export default function DashboardPage({
  plan,
  storeUrl = "",
  locale,
  appEmbedUuid = "a65575de-72bd-46c3-a21f-5b009e85bc92",
  hasCreatedFirstDiscount = false,
  hasCustomizedDesign = false,
  hasDismissedOnboarding = false,
  hasInstalledAppEmbed = false,
  hasModifiedAppBlockPlacement = false,
  hasModifiedAppEmbedFeaturedCollections = false,
  hasAddedCartDiscountBlock = false,
  rating = null,
  ratedAt = null,
  trialUsedForPlans = {},
  halloweenOffer = null
}) {
  // The offer banners read the install response, not a query, so they're there on the first render instead of
  // pushing the page down when they pop in (a layout shift Shopify counts against the app).
  const bannerMeta = { SubscriptionPlan: plan, TrialUsedForPlans: trialUsedForPlans, HalloweenOffer: halloweenOffer };
  const { data: onboardingStatus, refetch: refetchOnboarding } =
    useQuery(GET_ONBOARDING_STATUS(), { fetchPolicy: "cache-and-network" }) ||
    {};
  const { data: saleStatistics } =
    useQuery(GET_SALE_STATISTICS(), { fetchPolicy: "cache-and-network" }) || {};
  const [saveOnboarding] = useMutation(SAVE_ONBOARDING_STATUS());
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    hasDismissedOnboarding,
  );
  const navigate = useNavigate();
  const [showSaleTypeModal, setShowSaleTypeModal] = useState(false);

  // One theme picker (in the storefront status card) drives the status and the guide's theme editor links
  const themeStatus = useThemeExtensionStatus({ storeUrl, appEmbedUuid });

  const onboardingData = onboardingStatus?.onboardingStatus || {};
  const storeRating = onboardingData?.Rating ?? rating;
  const storeRatedAt = onboardingData?.RatedAt ?? ratedAt;
  const hasPerfectRating = storeRating === 5 && !!storeRatedAt;
  const isAppEmbedActive = themeStatus.appEmbedEnabled ?? onboardingData?.HasInstalledAppEmbed ?? hasInstalledAppEmbed;

  const onFocus = () => {
    refetchOnboarding();
    window.removeEventListener("focus", onFocus);
  };

  // Saved step completion, in STEP order
  const [stepCompletions, setStepCompletions] = useState([
    hasInstalledAppEmbed,
    hasCreatedFirstDiscount,
    hasCustomizedDesign,
    hasModifiedAppBlockPlacement,
    hasAddedCartDiscountBlock,
    hasModifiedAppEmbedFeaturedCollections,
  ]);

  useEffect(() => {
    if (onboardingStatus) {
      setStepCompletions(prev => {
        const updated = [...prev];
        updated[STEP.appEmbed] = onboardingData.HasInstalledAppEmbed;
        return updated;
      });
    } // eslint-disable-next-line
  }, [onboardingStatus]);

  const doSaveOnboarding = async (completions, dismiss = false) => {
    try {
      await saveOnboarding({
        variables: {
          input: {
            HasInstalledAppEmbed: completions[STEP.appEmbed],
            HasCreatedFirstDiscount: completions[STEP.firstDiscount],
            HasCustomizedDesign: completions[STEP.customizeDesign],
            HasDismissedOnboarding: dismiss,
            HasModifiedAppBlockPlacement: completions[STEP.blockPlacement],
            HasModifiedAppEmbedFeaturedCollections: completions[STEP.featuredCollections],
            HasAddedCartDiscountBlock: completions[STEP.cartDiscountBlock],
          },
        },
      });
    } catch (e) {
      console.error("failed saving onboarding: " + e);
    }
  };

  const saveStepCompletion = (index, completed) => {
    const updated = [...stepCompletions];
    updated[index] = completed;
    setStepCompletions(updated);
    doSaveOnboarding(updated);
  };

  // The circle ticks a step either way; a step's own button only ever marks it done
  const handleCompletionToggle = (index) => saveStepCompletion(index, !stepCompletions[index]);
  const markStepDone = (index) => {
    if (!stepCompletions[index]) saveStepCompletion(index, true);
  };

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    doSaveOnboarding(stepCompletions, true);
  };

  const handleSaleTypeSelected = (saleType) => {
    setShowSaleTypeModal(false);
    navigate(`/campaigns/create/${getSaleTypeUrlParam(saleType)}`);
  };

  const themeLink = (content, url, step) => ({
    content,
    url,
    external: true,
    onAction: step === undefined ? undefined : () => markStepDone(step),
  });

  const noAppBlocks = !themeStatus.supportsAppBlocks && (
    <Text variant="bodyMd" as="p" tone="caution">
      The selected theme does not support app blocks.{" "}
      <Link url={NON_OS2_PLACEMENT_ARTICLE} target="_blank">
        Learn how to set up custom placement for non-Online Store 2.0 themes.
      </Link>
    </Text>
  );

  // Block steps also count as done when the selected theme already has the block
  const steps = [
    {
      key: "appEmbed",
      title: "Turn on the app embed",
      description: (
        <>
          Connects Disco to your theme, so campaigns can show timers, bars and sale tags. Click below to open the
          theme editor, then click <ClickTarget>Save</ClickTarget> to activate the app embed.
        </>
      ),
      media: "media/disco-app-embed.png",
      completed: isAppEmbedActive,
      actions: [{
        ...themeLink("Turn on in theme", themeStatus.appEmbedDeepLink),
        onAction: () => window.addEventListener("focus", onFocus),
      }],
    },
    {
      key: "firstDiscount",
      title: "Create your first campaign",
      description: (
        <>
          Add discounts to your products and choose which widgets show while the campaign runs: timers, bars, sale
          tags or a sale page.{" "}
          <Link url="https://productessentials.helpscoutdocs.com/article/32-flash-sale-page" target="_blank">
            Learn more
          </Link>
        </>
      ),
      media: OFFER_DISCOUNT_IMAGE,
      completed: stepCompletions[STEP.firstDiscount],
      actions: [{ content: "Create campaign", onAction: () => setShowSaleTypeModal(true) }],
    },
    {
      key: "customizeDesign",
      optional: true,
      canToggle: true,
      title: "Customize the timer and bar",
      description: "Colors, fonts and size to match your brand.",
      media: CUSTOMIZE_THEME_IMAGE,
      completed: stepCompletions[STEP.customizeDesign],
      actions: [{ content: "Customize design", url: "/widgets", onAction: () => markStepDone(STEP.customizeDesign) }],
    },
    {
      key: "blockPlacement",
      optional: true,
      canToggle: true,
      title: "Reposition the timer and bar",
      description: "Place them as blocks wherever you want them in your theme.",
      media: "media/disco-countdown-timer-pos.png",
      completed: stepCompletions[STEP.blockPlacement]
        || themeStatus.isBlockEnabled("countdown_timer_block")
        || themeStatus.isBlockEnabled("announcement_bar_block"),
      actions: [
        themeLink("Place timer", themeStatus.blockDeepLink("countdown_timer_block"), STEP.blockPlacement),
        themeLink("Place bar", themeStatus.blockDeepLink("announcement_bar_block"), STEP.blockPlacement),
      ],
      notice: noAppBlocks || undefined,
    },
    {
      key: "cartDiscountBlock",
      optional: true,
      canToggle: true,
      title: "Show savings on the cart page",
      description: "Reminds shoppers what they save before they check out.",
      media: "media/disco-cart-discount.png",
      completed: stepCompletions[STEP.cartDiscountBlock] || themeStatus.isBlockEnabled("cart_total_discounts"),
      actions: [themeLink("Place cart block", themeStatus.blockDeepLink("cart_total_discounts"), STEP.cartDiscountBlock)],
      notice: noAppBlocks || undefined,
    },
    {
      key: "featuredCollections",
      optional: true,
      canToggle: true,
      title: "Sale tags on home page collections",
      description: (
        <>
          In the theme editor, click <ClickTarget>Select collections</ClickTarget> to choose which collections show
          sale tags.
        </>
      ),
      media: OFFER_DISCOUNT_IMAGE,
      completed: stepCompletions[STEP.featuredCollections],
      actions: [themeLink("Select collections", buildAppEmbedExpandDeepLink(storeUrl, appEmbedUuid, "app_embed"), STEP.featuredCollections)],
    },
  ];

  return (
    <Frame>
      <Page
        title="Dashboard"
        forceRender
        primaryAction={{
          content: "Create sale",
          onAction: () => setShowSaleTypeModal(true)
        }}
      >
        <Layout>
          <DraftResumeBanner />
          {
            <Layout.Section>
              <BlockStack gap="400">
                <SecurityNoticeBanner />
                {/* Free stores see the Early Bird BFCM Offer until it ends, everyone else the Early Bird BFCM Deal */}
                <HalloweenOfferBanner meta={bannerMeta} />
                <BFCMSaleBanner meta={bannerMeta} />
              </BlockStack>
            </Layout.Section>
            /*!activeSalesCount && !scheduledSaleCount && (
              <Layout.Section>
                <BlackWeekSaleSurvey />
              </Layout.Section>
            )*/
          }
          <Layout.Section>
            <BlockStack gap="400">
              <CampaignOverview statistics={saleStatistics?.saleStatistics} />
              {onboardingDismissed ? (
                <RecentCampaigns locale={locale} onCreateSale={() => setShowSaleTypeModal(true)} />
              ) : (
                <SetupGuide
                  steps={steps}
                  onToggleStep={(key) => handleCompletionToggle(STEP[key])}
                  onDismiss={dismissOnboarding}
                />
              )}
            </BlockStack>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <ThemeStatus
                themeOptions={themeStatus.themeOptions}
                selectedThemeId={themeStatus.selectedThemeId}
                onThemeChange={themeStatus.selectTheme}
                isAppEmbedActive={isAppEmbedActive}
                appEmbedDeepLink={themeStatus.appEmbedDeepLink}
                supportsAppBlocks={themeStatus.supportsAppBlocks}
                blocks={themeStatus.blocks}
              />
              <HelpCard openSupportBeacon={openBeacon} />
              {hasInstalledAppEmbed && hasCreatedFirstDiscount && !hasPerfectRating && (
                <AppRatingWidget
                  showToast={window?.shopify?.toast?.show}
                  onRequestReview={window.shopify?.reviews?.request}
                  showGradient={false}
                  enableShake={true}
                />
              )}
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>

      <SaleTypeModal
        open={showSaleTypeModal}
        onClose={() => setShowSaleTypeModal(false)}
        onSelect={handleSaleTypeSelected}
        plan={plan}
      />
    </Frame>
  );
}
